import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BrIconComponent } from '../../shared/ui/icon/icon.component';
import { MatTooltipModule } from '@angular/material/tooltip';

import { AuthService } from '../../services/auth.service';
import { downloadFile } from '../../shared/download.util';
import { TransferTable } from '../../services/table-import-export.service';
import { ActivatedRoute, Router } from '@angular/router';
import { DedupeTable } from '../../pages/dedupe/dedupe.service';
import {
  TableImportDialogComponent, tableImportDialogOptions,
} from '../table-import-dialog/table-import-dialog.component';

/**
 * Which permission each table's import endpoints enforce - as the SERVER
 * enforces it, read off its TableTransferController.
 *
 * Kept here, once, because the pages had it right by accident of copying: the
 * Leads board checked `Import.Run` and the other three `Import.Manage`, each
 * correctly, each in its own template. A page that copied the wrong neighbour
 * would offer a button to somebody the server then answers with a 403 - or
 * hide it from somebody allowed to use it. The gate is a fact about the table,
 * so it lives beside the table's name, not on the page.
 */
const IMPORT_PERMISSION: Readonly<Record<TransferTable, string>> = {
  leads: 'Import.Run',
  customers: 'Import.Manage',
  'rolloff-hires': 'Import.Manage',
};

/** The tables that have a de-duplication at all. */
const DEDUPE_TABLES: readonly string[] = ['leads', 'customers', 'rolloff-hires'];

const isDedupeTable = (table: string): table is DedupeTable => DEDUPE_TABLES.includes(table);

/**
 * Import and Dedup, for any table that has them.
 *
 * WHY THIS EXISTS. The same two buttons and the same action entry points had
 * been written on the Leads board, the customers table and the roll-off list -
 * copies of "open the import dialog told which table it is, reload if it
 * changed something" - and the roll-off board was about to be another. What
 * differed between them was the table's name, what the table is called on
 * screen, and what each page reloads. The first two are inputs; the third is
 * the page's answer to `changed`.
 *
 * That the table's name is ALL a caller hands over is not an economy. The
 * import dialog asks the table for its own schema, which is what let the
 * customers table use the same dialog as leads - and it cannot drift from any
 * page, because the columns never came from a page. A caller that started
 * passing columns in would be rebuilding the separate importer this replaced.
 *
 * EXPORT IS HERE TOO, for the callers whose export has nothing to ask. Three
 * of the four wrote the same button over the same "download the whole table"
 * endpoint; the roll-off board wanting it as well would have been the fourth
 * copy. A caller that hands over no endpoint gets no button, which is how the
 * Leads board keeps its own: that one opens a dialog to ask WHICH rows first,
 * and a component that took both shapes would be a flag per caller.
 *
 * What it still does NOT own is Delete all - destructive, permissioned
 * separately on the account rather than the table, and not something every
 * table has. That stays with the pages that have it.
 *
 * Dedup is offered to anyone on the page, as it was on all three
 * pages before: scanning is View-gated on the server and merging Manage-gated,
 * so the routed review page allows viewing without merging.
 */
@Component({
  selector: 'app-table-transfer-actions',
  standalone: true,
  imports: [CommonModule, BrIconComponent, MatTooltipModule],
  template: `
    <!-- Rows OUT, for a table whose export is simply the whole table. Given an
         endpoint it is the same button and the same download wherever it is
         dropped; given none it is not drawn, which is how the Leads board
         keeps its own Export - that one asks which rows first. -->
    <button type="button" class="br-btn br-btn--outline-secondary br-btn--sm"
            *ngIf="exportUrl" [disabled]="exporting" (click)="exportCsv()"
            [matTooltip]="'Export every ' + title + ' to CSV'">
      <br-icon>download</br-icon> {{ exporting ? 'Exporting…' : 'Export' }}
    </button>
    <span class="tta-error" *ngIf="exportError">
      <br-icon>error_outline</br-icon> {{ exportError }}
    </span>
    <button type="button" class="br-btn br-btn--outline-secondary br-btn--sm"
            *ngIf="canImport" (click)="openImport()"
            [matTooltip]="'Import ' + title + ' from CSV / Excel'">
      <br-icon>upload</br-icon> Import
    </button>
    <!-- Beside Import on purpose: an import inserts and never matches, so this
         is where the repeats it leaves behind are folded back together. -->
    <button type="button" class="br-btn br-btn--outline-secondary br-btn--sm"
            *ngIf="canDedupe" (click)="openDedupe()"
            matTooltip="Remove duplicates based on custom criteria.">
      <br-icon>filter_none</br-icon> Dedup
    </button>
  `,
  styles: [`
    /* No box of its own. The buttons have to be flex items of whatever row
       they are dropped into - a table's command band folds its actions one
       button at a time, and a wrapper box would fold as one block of two. */
    :host { display: contents; }

    .tta-error {
      display: inline-flex;
      align-items: center;
      gap: var(--br-space-1);
      color: var(--br-color-danger, #b3261e);
      font-size: 0.875rem;
    }
  `],
})
export class TableTransferActionsComponent {
  /** Which table the dialogs act on. */
  @Input({ required: true }) table!: TransferTable;

  /** What the table is called on screen, plural - "roll-off hires". Nothing
   *  branches on it; it is the words in the tooltips and the dialog titles. */
  @Input() title = '';

  /**
   * Where the whole table is written out, and what the file is called. Given
   * together or not at all: without them no Export button is drawn.
   *
   * The endpoint rather than a flag, because the caller already knows it and a
   * lookup table here would be a second place for a table's transfer route to
   * be recorded.
   */
  @Input() exportUrl = '';
  @Input() exportFileName = 'export.csv';

  /** An import added rows or a merge folded some. The page decides what to
   *  read again - a list, a board, both. */
  @Output() changed = new EventEmitter<void>();

  constructor(
    private dialog: MatDialog,
    private auth: AuthService,
    private http: HttpClient,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  exporting = false;
  exportError: string | null = null;

  /**
   * The whole table, as the server writes it.
   *
   * Not the page on screen and not the filter in force: an export is how rows
   * leave, and a file holding one page of fifty would quietly leave the rest
   * behind. The download itself is the shared one, so a browser that refuses
   * it fails the same way here as everywhere else.
   */
  exportCsv(): void {
    if (this.exporting || !this.exportUrl) return;
    this.exporting = true;
    this.exportError = null;
    downloadFile(this.http, this.exportUrl, this.exportFileName).subscribe({
      next: () => { this.exporting = false; },
      error: () => { this.exporting = false; this.exportError = 'Export failed.'; },
    });
  }

  get canImport(): boolean {
    return this.auth.hasPermission(IMPORT_PERMISSION[this.table]);
  }

  get canDedupe(): boolean {
    return isDedupeTable(this.table);
  }

  openImport(): void {
    this.dialog.open(TableImportDialogComponent, {
      ...tableImportDialogOptions,
      data: { table: this.table, title: this.title },
    })
      .afterClosed()
      .subscribe(changed => { if (changed) this.changed.emit(); });
  }

  openDedupe(): void {
    const table = this.table;
    if (!isDedupeTable(table)) return;
    this.router.navigate(['duplicates'], { relativeTo: this.route, queryParamsHandling: 'preserve' });
  }
}
